import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle2,
  ChevronDown,
  MoreHorizontal,
  Pause,
  Play,
  RotateCcw,
  Trash2,
  XCircle,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type {
  CarePathwayStatus,
  CarePathwayViewModel,
} from '@/features/patient-record/types';

import { PathwayStepList, type StepCompletionPayload, type StepSkipPayload } from './PathwayStepList';

interface CarePathwayRowProps {
  readonly guid: string;
  readonly carePathway: CarePathwayViewModel;
  readonly updatingStepId: string | null;
  readonly statusBusy: boolean;
  readonly onRequestStepComplete: (payload: StepCompletionPayload) => void;
  readonly onRequestStepSkip: (payload: StepSkipPayload) => void;
  readonly onMarkStepIncomplete: (stepId: string) => void;
  readonly onChangeStatus: (status: 'ACTIVE' | 'PAUSED') => void;
  readonly onRequestClose: (status: 'COMPLETED' | 'CANCELLED') => void;
  readonly onRemove: () => void;
}

export function CarePathwayRow({
  guid,
  carePathway,
  updatingStepId,
  statusBusy,
  onRequestStepComplete,
  onRequestStepSkip,
  onMarkStepIncomplete,
  onChangeStatus,
  onRequestClose,
  onRemove,
}: CarePathwayRowProps): ReactElement {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);

  const status = carePathway.status;
  const isPaused = status === 'PAUSED';
  const isClosed = status === 'COMPLETED' || status === 'CANCELLED';

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => {
            setOpen((v) => !v);
          }}
          aria-expanded={open}
          aria-label={open ? t('patient.carePathways.collapse') : t('patient.carePathways.expand')}
          className="flex flex-1 min-w-0 items-center gap-2 text-left"
        >
          <ChevronDown
            aria-hidden="true"
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform shrink-0',
              !open && '-rotate-90',
            )}
          />
          <span className="text-sm font-semibold text-card-foreground truncate">
            {carePathway.name}
          </span>
        </button>
        <div className="flex items-center gap-3 shrink-0">
          {carePathway.stepsTotal > 0 && (
            <span className="text-xs text-muted-foreground tabular-nums">
              {String(carePathway.stepsCompleted)}/{String(carePathway.stepsTotal)}{' '}
              {t('patient.carePathways.stepsSuffix')}
            </span>
          )}
          <PathwayStatusBadge status={status} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild disabled={statusBusy}>
              <Button
                size="sm"
                variant="ghost"
                aria-label={t('patient.carePathways.moreActions')}
                className="h-7 w-7 p-0"
              >
                <MoreHorizontal className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[12rem]">
              {!isClosed &&
                (isPaused ? (
                  <DropdownMenuItem
                    onSelect={() => {
                      onChangeStatus('ACTIVE');
                    }}
                  >
                    <Play className="h-3.5 w-3.5" aria-hidden="true" />
                    {t('patient.carePathways.resume')}
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    onSelect={() => {
                      onChangeStatus('PAUSED');
                    }}
                  >
                    <Pause className="h-3.5 w-3.5" aria-hidden="true" />
                    {t('patient.carePathways.pause')}
                  </DropdownMenuItem>
                ))}
              {!isClosed && (
                <DropdownMenuItem
                  onSelect={() => {
                    onRequestClose('COMPLETED');
                  }}
                >
                  <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                  {t('patient.carePathways.completePathway')}
                </DropdownMenuItem>
              )}
              {!isClosed && (
                <DropdownMenuItem
                  onSelect={() => {
                    onRequestClose('CANCELLED');
                  }}
                >
                  <XCircle className="h-3.5 w-3.5" aria-hidden="true" />
                  {t('patient.carePathways.cancel')}
                </DropdownMenuItem>
              )}
              {isClosed && (
                <DropdownMenuItem
                  onSelect={() => {
                    onChangeStatus('ACTIVE');
                  }}
                >
                  <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                  {t('patient.carePathways.reactivate')}
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onSelect={onRemove}
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                {t('patient.carePathways.remove')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      {open && carePathway.steps.length > 0 && (
        <PathwayStepList
          guid={guid}
          steps={carePathway.steps}
          updatingStepId={updatingStepId}
          pathwayClosed={isClosed}
          onRequestComplete={onRequestStepComplete}
          onRequestSkip={onRequestStepSkip}
          onMarkIncomplete={onMarkStepIncomplete}
        />
      )}
      {isClosed && carePathway.completionReason !== null && (
        <div className="ml-6 rounded-md border border-border bg-muted/30 px-2.5 py-1.5">
          <p className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
            {t('patient.carePathways.closeReasonLabel')}
          </p>
          <p className="text-xs text-foreground mt-0.5 whitespace-pre-wrap">
            {carePathway.completionReason}
          </p>
        </div>
      )}
    </div>
  );
}

interface PathwayStatusBadgeProps {
  readonly status: CarePathwayStatus;
}

const STATUS_STYLES: Record<CarePathwayStatus, { bg: string; text: string; dot: string }> = {
  ACTIVE:    { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  PAUSED:    { bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-500' },
  COMPLETED: { bg: 'bg-sky-50',     text: 'text-sky-700',     dot: 'bg-sky-500' },
  CANCELLED: { bg: 'bg-muted',      text: 'text-muted-foreground', dot: 'bg-muted-foreground' },
};

function PathwayStatusBadge({ status }: PathwayStatusBadgeProps): ReactElement {
  const { t } = useTranslation();
  const styles = STATUS_STYLES[status];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium',
        styles.bg,
        styles.text,
      )}
    >
      <span aria-hidden="true" className={cn('h-1.5 w-1.5 rounded-full', styles.dot)} />
      {t(`patient.carePathways.status.${status}`)}
    </span>
  );
}
